import { useState, useRef } from 'react';
import type { Lead } from '@/mocks/admin';

interface GerarPropostaModalProps {
  lead: Lead;
  onClose: () => void;
}

export default function GerarPropostaModal({ lead, onClose }: GerarPropostaModalProps) {
  const [gerando, setGerando] = useState(false);
  const [gerado, setGerado] = useState(false);
  const [valor, setValor] = useState('4.997');
  const [valorPrazo, setValorPrazo] = useState('5.496,70');
  const [validade, setValidade] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  });
  const propostaRef = useRef<HTMLDivElement>(null);

  const handleGerarPDF = async () => {
    if (!propostaRef.current) return;
    setGerando(true);

    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const opt = {
        margin: 0,
        filename: `Proposta_TR60D_${lead.nome.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      };

      await html2pdf().set(opt).from(propostaRef.current).save();
      setGerado(true);
      setTimeout(() => setGerado(false), 3000);
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
    } finally {
      setGerando(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden shadow-2xl">

        {/* HEADER */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-amber-100">
                <i className="ri-file-text-line text-amber-600 text-xl" />
              </div>
              <div>
                <h2 className="text-gray-900 font-black text-lg">Gerar Proposta Comercial</h2>
                <p className="text-gray-400 text-sm">{lead.nome} — {lead.empresa}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-gray-400 text-lg" />
            </button>
          </div>
        </div>

        {/* CONTEÚDO SCROLLÁVEL */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="bg-white border border-gray-100 rounded-xl p-4">
              <label className="text-gray-500 text-xs font-semibold mb-1 block">Valor à Vista (R$)</label>
              <input
                type="text"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-700 text-sm focus:outline-none focus:border-amber-400"
              />
            </div>
            <div className="bg-white border border-gray-100 rounded-xl p-4">
              <label className="text-gray-500 text-xs font-semibold mb-1 block">Valor à Prazo (R$)</label>
              <input
                type="text"
                value={valorPrazo}
                onChange={(e) => setValorPrazo(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-700 text-sm focus:outline-none focus:border-amber-400"
              />
            </div>
            <div className="bg-white border border-gray-100 rounded-xl p-4">
              <label className="text-gray-500 text-xs font-semibold mb-1 block">Validade</label>
              <input
                type="text"
                value={validade}
                onChange={(e) => setValidade(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-700 text-sm focus:outline-none focus:border-amber-400"
              />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-amber-500 px-4 py-2 flex items-center gap-2">
              <i className="ri-eye-line text-white text-sm" />
              <span className="text-white text-xs font-bold">Preview da Proposta</span>
            </div>
            <div className="p-4 overflow-auto max-h-[60vh]">
              <PropostaHTML ref={propostaRef} lead={lead} valor={valor} valorPrazo={valorPrazo} validade={validade} />
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white">
          <button
            onClick={handleGerarPDF}
            disabled={gerando}
            className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white font-bold text-sm py-3.5 rounded-xl transition-colors cursor-pointer"
          >
            {gerando ? (
              <><i className="ri-loader-2-line animate-spin" /><span>Gerando PDF...</span></>
            ) : gerado ? (
              <><i className="ri-check-line text-emerald-300" /><span className="text-emerald-300">PDF gerado com sucesso!</span></>
            ) : (
              <><i className="ri-file-pdf-line text-lg" /><span>Baixar Proposta em PDF</span></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

import { forwardRef } from 'react';

interface PropostaHTMLProps {
  lead: Lead;
  valor: string;
  valorPrazo: string;
  validade: string;
}

const PropostaHTML = forwardRef<HTMLDivElement, PropostaHTMLProps>(({ lead, valor, valorPrazo, validade }, ref) => {
  const hoje = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div ref={ref} style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", background: '#fff', color: '#1e293b', fontSize: '10px', lineHeight: 1.6, width: '210mm', minHeight: '297mm', padding: '0', boxSizing: 'border-box' }}>

      {/* HEADER */}
      <table cellPadding={0} cellSpacing={0} style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tr>
          <td style={{ background: '#0D0D0D', padding: '22px 24px 18px', textAlign: 'center' as const }}>
            <div style={{ color: '#F97316', fontWeight: 900, fontSize: '13px', marginBottom: '8px', letterSpacing: '1px' }}>PROPOSTA COMERCIAL</div>
            <div style={{ color: '#FFFFFF', fontWeight: 900, fontSize: '42px', lineHeight: 1, letterSpacing: '-1px' }}>TRANSFORMAÇÃO</div>
            <div style={{ color: '#FFFFFF', fontWeight: 900, fontSize: '42px', lineHeight: 1, letterSpacing: '-1px' }}>
              REAL <span style={{ color: '#F97316' }}>60D</span>
            </div>
            <div style={{ color: '#AAAAAA', fontSize: '12px', marginTop: '8px' }}>Do diagnóstico à execução. Resultados reais em 60 dias.</div>
          </td>
        </tr>
        <tr>
          <td style={{ background: '#F97316', padding: '10px 24px', textAlign: 'center' as const }}>
            <span style={{ color: '#FFFFFF', fontWeight: 700, fontSize: '11px', letterSpacing: '0.5px' }}>CONSULTORIA PRESENCIAL E PRÁTICA • 6 MÓDULOS + BÔNUS • VAGAS LIMITADAS</span>
          </td>
        </tr>
        <tr>
          <td style={{ padding: '16px 24px 18px', background: '#FFFFFF' }}>
            <div style={{ color: '#888888', fontSize: '10px', marginBottom: '4px' }}>Preparado para:</div>
            <div style={{ fontWeight: 900, fontSize: '18px', color: '#1e293b', marginBottom: '2px' }}>{lead.nome}</div>
            <div style={{ color: '#888888', fontSize: '12px' }}>{lead.empresa || '[Empresa]'}</div>
            <div style={{ borderTop: '1px solid #E0E0E0', marginTop: '16px', paddingTop: '10px' }}>
              <span style={{ color: '#888888', fontSize: '10px' }}>Manaus, {hoje}</span>
            </div>
            <div style={{ color: '#888888', fontSize: '10px', fontStyle: 'italic' as const, marginTop: '2px' }}>Válida por 7 dias corridos.</div>
          </td>
        </tr>
      </table>

      {/* CONTENT */}
      <div style={{ padding: '0 24px 16px' }}>

        {/* 01 CONTEXTO */}
        <div style={{ marginBottom: '16px' }}>
          <h2 style={{ fontWeight: 900, fontSize: '12px', color: '#F97316', marginBottom: '8px', paddingBottom: '4px', borderBottom: '2px solid #F97316' }}>
            01  /  O CONTEXTO
          </h2>
          <p style={{ fontSize: '11px', fontWeight: 800, color: '#1e293b', marginBottom: '6px' }}>A maioria das empresas não quebra por falta de clientes.</p>
          <p style={{ fontSize: '10px', color: '#374151', marginBottom: '10px', lineHeight: 1.7 }}>
            Elas quebram por falta de estrutura. Gestão improvisada, fluxo de caixa descontrolado, equipe sem processos e uma área comercial que depende 100% do dono. O resultado? Um negócio que cansa em vez de crescer.
          </p>

          {/* Tabela 7 problemas */}
          <table cellPadding={0} cellSpacing={0} style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
            <tr>
              <td style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px 0 0 8px', padding: '12px', width: '50%', verticalAlign: 'top' }}>
                <p style={{ fontSize: '11px', fontWeight: 800, color: '#1e293b', marginBottom: '8px' }}>7 problemas que travam negócios</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                  {[
                    'Dono sobrecarregado e sozinho',
                    'Desorganização financeira',
                    'Sem processo comercial definido',
                    'Equipe sem autonomia',
                    'Dependência de poucos clientes',
                    'Falta de indicadores de gestão',
                    'Ausência de tecnologia e IA',
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: '5px', alignItems: 'center', fontSize: '9px', color: '#374151' }}>
                      <span style={{ color: '#ef4444', fontWeight: 900 }}>●</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </td>
              <td style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderLeft: 'none', borderRadius: '0 8px 8px 0', padding: '12px', verticalAlign: 'middle' }}>
                <p style={{ fontSize: '9px', color: '#64748b', lineHeight: 1.7, fontStyle: 'italic' as const }}>
                  Se o seu negócio trava porque <strong style={{ color: '#1e293b' }}>você é o único que resolve tudo</strong>, a solução não é trabalhar mais.<br /><br />
                  <strong style={{ color: '#9a3412' }}>É estruturar. É o que fazemos no Transformação Real 60D.</strong>
                </p>
              </td>
            </tr>
          </table>
        </div>

        {/* 02 MÉTODO */}
        <div style={{ marginBottom: '16px' }}>
          <h2 style={{ fontWeight: 900, fontSize: '12px', color: '#F97316', marginBottom: '8px', paddingBottom: '4px', borderBottom: '2px solid #F97316' }}>
            02  /  O MÉTODO
          </h2>
          <p style={{ fontSize: '11px', fontWeight: 800, color: '#1e293b', marginBottom: '10px' }}>Não é curso. É consultoria com entregáveis.</p>

          <table cellPadding={0} cellSpacing={0} style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tr>
              {[
                { icon: '✦', title: 'Diagnóstico real', desc: 'Começamos entendendo onde seu negócio está travado antes de propor qualquer solução.' },
                { icon: '✦', title: 'Entregáveis práticos', desc: 'Cada módulo termina com uma ferramenta aplicável imediatamente na sua operação.' },
                { icon: '✦', title: 'Acompanhamento real', desc: 'Especialistas ao seu lado durante toda a jornada de 60 dias, sem te deixar sozinho.' },
              ].map((item, i) => (
                <td key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 12px', textAlign: 'center' as const, verticalAlign: 'top' }}>
                  <div style={{ fontSize: '12px', color: '#F97316', fontWeight: 900, marginBottom: '4px' }}>{item.icon}</div>
                  <div style={{ fontSize: '10px', fontWeight: 800, color: '#1e293b', marginBottom: '4px' }}>{item.title}</div>
                  <div style={{ fontSize: '8.5px', color: '#64748b', lineHeight: 1.5 }}>{item.desc}</div>
                </td>
              ))}
            </tr>
          </table>
        </div>

        {/* 03 PROGRAMA */}
        <div style={{ marginBottom: '16px' }}>
          <h2 style={{ fontWeight: 900, fontSize: '12px', color: '#F97316', marginBottom: '8px', paddingBottom: '4px', borderBottom: '2px solid #F97316' }}>
            03  /  O PROGRAMA
          </h2>
          <p style={{ fontSize: '11px', fontWeight: 800, color: '#1e293b', marginBottom: '4px' }}>6 Módulos + 1 Bônus.</p>
          <p style={{ fontSize: '10px', color: '#64748b', marginBottom: '10px' }}>Uma transformação completa, módulo a módulo, com ferramentas e entregáveis concretos.</p>

          {[
            { num: '01', title: 'MAPA DE GARGALOS E ALAVANCAS', objetivo: 'Identificar onde o negócio está travado e onde pode crescer mais rápido.', items: ['Diagnóstico estrutural do negócio', 'Análise de mercado e concorrência', 'Identificação de gargalos operacionais', 'Identificação de oportunidades de crescimento', 'Definição de metas estratégicas'], entregavel: 'Raio-X completo da empresa', color: '#7c3aed' },
            { num: '02', title: 'GESTÃO E ESTRUTURA OPERACIONAL', objetivo: 'Organizar o funcionamento da empresa com processos e rotinas claras.', items: ['Definição de estrutura organizacional', 'Mapeamento de processos', 'Criação de rotinas operacionais', 'Criação de indicadores de gestão', 'Gestão por metas'], entregavel: 'Estrutura organizacional clara e processos definidos', color: '#0284c7' },
            { num: '03', title: 'ORGANIZAÇÃO FINANCEIRA', objetivo: 'Criar controle real sobre o dinheiro do negócio.', items: ['Estruturação do fluxo de caixa', 'DRE simplificado', 'Controle de custos', 'Margem de contribuição e ponto de equilíbrio', 'Formação de preço correta'], entregavel: 'Planejamento Financeiro', color: '#059669' },
            { num: '04', title: 'ESTRUTURA COMERCIAL', objetivo: 'Transformar um comercial improvisado em uma operação com processo, rotina e previsibilidade de vendas.', items: ['Funil comercial adaptado à sua operação', 'Organização e qualificação de leads', 'Processo de atendimento e avanço de oportunidades', 'Scripts para abordagem, follow-up e fechamento', 'Métricas para acompanhar conversão e performance'], entregavel: 'Plano prático da sua estrutura comercial', color: '#ea580c' },
            { num: '05', title: 'INOVAÇÃO E EFICIÊNCIA EMPRESARIAL', objetivo: 'Identificar novas oportunidades de crescimento e diferenciação competitiva.', items: ['Revisão do modelo de negócio', 'Identificação de diferenciação competitiva', 'Desenvolvimento de novos produtos ou serviços', 'Estratégias de expansão'], entregavel: 'Plano de Implementação de Inovação', color: '#0d9488' },
            { num: '06', title: 'INTELIGÊNCIA ARTIFICIAL APLICADA AO NEGÓCIO', objetivo: 'Aumentar produtividade e eficiência com ferramentas de IA.', items: ['Uso de IA no atendimento ao cliente', 'Geração automática de conteúdos', 'Análise de dados com IA', 'Automação de tarefas operacionais', 'Criação de assistentes empresariais'], entregavel: 'Ferramentas de IA implementadas', color: '#e11d48' },
          ].map(mod => (
            <div key={mod.num} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 12px', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <div style={{ width: '22px', height: '22px', borderRadius: '4px', background: mod.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#fff', fontSize: '9px', fontWeight: 900 }}>{mod.num}</span>
                </div>
                <span style={{ fontSize: '10px', fontWeight: 800, color: '#1e293b' }}>MÓDULO {mod.num} — {mod.title}</span>
              </div>
              <p style={{ fontSize: '9px', color: '#64748b', marginBottom: '5px', fontStyle: 'italic' as const }}>Objetivo: {mod.objetivo}</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 8px', marginBottom: '4px' }}>
                {mod.items.map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: '4px', alignItems: 'center', fontSize: '8px', color: '#374151' }}>
                    <span style={{ color: '#10b981', fontWeight: 900 }}>✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: '8px', fontWeight: 700, color: mod.color }}>✦ Entregável: {mod.entregavel}</p>
            </div>
          ))}

          {/* MÓDULO BÔNUS */}
          <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 12px', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '4px', background: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ color: '#fff', fontSize: '8px', fontWeight: 900 }}>★</span>
              </div>
              <span style={{ fontSize: '10px', fontWeight: 800, color: '#92400e' }}>★ MÓDULO BÔNUS EXCLUSIVO ★</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '4px', background: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ color: '#fff', fontSize: '9px', fontWeight: 900 }}>07</span>
              </div>
              <span style={{ fontSize: '10px', fontWeight: 800, color: '#1e293b' }}>JURÍDICO PARA EMPRESAS</span>
            </div>
            <p style={{ fontSize: '9px', color: '#64748b', marginBottom: '5px', fontStyle: 'italic' as const }}>
              Reduzir riscos, acelerar processos e proteger relações com apoio de estrutura jurídica inteligente. Inclui biblioteca de documentos, padronização contratual e plataforma jurídica empresarial.
            </p>
            <p style={{ fontSize: '8px', fontWeight: 700, color: '#4f46e5' }}>✦ Entregável: Mapa de eficiência jurídica para reduzir riscos, ganhar produtividade e melhorar a operação.</p>
          </div>
        </div>

        {/* 04 INVESTIMENTO */}
        <div style={{ marginBottom: '16px' }}>
          <h2 style={{ fontWeight: 900, fontSize: '12px', color: '#F97316', marginBottom: '8px', paddingBottom: '4px', borderBottom: '2px solid #F97316' }}>
            04  /  INVESTIMENTO
          </h2>
          <p style={{ fontSize: '10px', color: '#64748b', marginBottom: '10px', fontStyle: 'italic' as const }}>O custo de não resolver é sempre maior.</p>

          {/* Tabela INVESTIMENTO */}
          <table cellPadding={0} cellSpacing={0} style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
            <tr>
              {/* Coluna O QUE VOCÊ RECEBE */}
              <td style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px 0 0 8px', padding: '14px', width: '55%', verticalAlign: 'top' }}>
                <p style={{ fontSize: '11px', fontWeight: 800, color: '#166534', marginBottom: '8px' }}>O QUE VOCÊ RECEBE</p>
                {[
                  '6 módulos com especialistas dedicados',
                  'Módulo bônus: Jurídico para Empresas',
                  'Ferramentas exclusivas por módulo',
                  'Entregáveis práticos e aplicáveis',
                  'Acompanhamento durante 60 dias',
                  'Grupo de suporte com os especialistas',
                  'Acesso à plataforma do programa',
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '9px', color: '#14532d', marginBottom: '4px' }}>
                    <span style={{ color: '#10b981', fontWeight: 900 }}>✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </td>
              {/* Coluna INVESTIMENTO */}
              <td style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: 'none', borderRadius: '0 8px 8px 0', padding: '14px', verticalAlign: 'top' }}>
                <p style={{ fontSize: '11px', fontWeight: 800, color: '#1e293b', marginBottom: '10px', textAlign: 'center' as const }}>INVESTIMENTO</p>

                {/* À VISTA */}
                <div style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', borderRadius: '10px', padding: '14px', textAlign: 'center' as const, marginBottom: '8px' }}>
                  <p style={{ fontSize: '8px', fontWeight: 700, color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase' as const, marginBottom: '4px' }}>À VISTA</p>
                  <p style={{ fontSize: '26px', fontWeight: 900, color: '#fff', lineHeight: 1 }}>R$ {valor}</p>
                  <p style={{ fontSize: '9px', color: 'rgba(255,255,255,0.85)', marginTop: '4px' }}>Pix ou até 3x sem juros no cartão</p>
                </div>

                {/* À PRAZO */}
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '12px', textAlign: 'center' as const }}>
                  <p style={{ fontSize: '8px', fontWeight: 700, color: '#9a3412', textTransform: 'uppercase' as const, marginBottom: '4px' }}>À PRAZO</p>
                  <p style={{ fontSize: '18px', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>R$ {valorPrazo}</p>
                  <p style={{ fontSize: '9px', color: '#64748b', marginTop: '4px' }}>De 4x a 12x com juros no cartão</p>
                </div>
              </td>
            </tr>
          </table>
        </div>

        {/* 05 PRÓXIMOS PASSOS */}
        <div style={{ marginBottom: '16px' }}>
          <h2 style={{ fontWeight: 900, fontSize: '12px', color: '#F97316', marginBottom: '8px', paddingBottom: '4px', borderBottom: '2px solid #F97316' }}>
            05  /  PRÓXIMOS PASSOS
          </h2>
          <p style={{ fontSize: '10px', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>Como começar.</p>

          {/* Tabela 3 passos */}
          <table cellPadding={0} cellSpacing={0} style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px' }}>
            <tr>
              {[
                { num: '01', title: 'Confirme sua vaga', desc: 'Realize o pagamento para garantir sua participação na próxima turma.' },
                { num: '02', title: 'Onboarding', desc: 'Você receberá acesso à plataforma e ao grupo com os especialistas.' },
                { num: '03', title: 'Começa a transformação', desc: 'Em 60 dias, sua empresa terá uma estrutura diferente. Com processos, dados e autonomia.' },
              ].map((item, i) => (
                <td key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 12px', textAlign: 'center' as const, verticalAlign: 'top' }}>
                  <div style={{ background: '#f59e0b', color: '#fff', fontSize: '10px', fontWeight: 900, width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 6px', flexShrink: 0 }}>{item.num}</div>
                  <div style={{ fontSize: '10px', fontWeight: 800, color: '#1e293b', marginBottom: '4px' }}>{item.title}</div>
                  <div style={{ fontSize: '8.5px', color: '#64748b', lineHeight: 1.5 }}>{item.desc}</div>
                </td>
              ))}
            </tr>
          </table>

          <p style={{ fontSize: '10px', fontWeight: 700, color: '#9a3412', marginBottom: '4px' }}>Vagas limitadas. Próxima turma em breve.</p>
          <p style={{ fontSize: '9px', color: '#64748b' }}>Dúvidas? Entre em contato:</p>
        </div>

        {/* CONTATO */}
        <div style={{ marginBottom: '16px' }}>
          <table cellPadding={0} cellSpacing={0} style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tr>
              <td style={{ padding: '12px', textAlign: 'center' as const, borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
                <table cellPadding={0} cellSpacing={0} style={{ width: 'auto', margin: '0 auto', borderCollapse: 'collapse' }}>
                  <tr>
                    {[
                      { label: 'WhatsApp', value: '(92) 98439-8338' },
                      { label: 'Instagram', value: '@xlab.studio' },
                      { label: 'Site', value: 'www.xlabstudio.com.br' },
                    ].map((item, i) => (
                      <td key={i} style={{ textAlign: 'center' as const, padding: '0 20px', borderRight: i < 2 ? '1px solid #e2e8f0' : 'none' }}>
                        <div style={{ fontSize: '9px', fontWeight: 700, color: '#9a3412' }}>{item.label}</div>
                        <div style={{ fontSize: '10px', fontWeight: 800, color: '#1e293b' }}>{item.value}</div>
                      </td>
                    ))}
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </div>

        {/* ASSINATURAS */}
        <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: '60px' }}>
            {/* Assinatura 1 */}
            <div style={{ textAlign: 'center' as const, minWidth: '130px' }}>
              <div style={{ borderBottom: '1px solid #1e293b', marginBottom: '6px', paddingBottom: '48px' }}></div>
              <p style={{ fontSize: '10px', fontWeight: 800, color: '#1e293b' }}>Léo David Cunha</p>
              <p style={{ fontSize: '8px', color: '#64748b' }}>Representante — X Lab Studio</p>
              <p style={{ fontSize: '8px', color: '#64748b' }}>Empresa Realizadora do Programa</p>
            </div>
            {/* Assinatura 2 */}
            <div style={{ textAlign: 'center' as const, minWidth: '130px' }}>
              <div style={{ borderBottom: '1px solid #1e293b', marginBottom: '6px', paddingBottom: '48px' }}></div>
              <p style={{ fontSize: '10px', fontWeight: 800, color: '#1e293b' }}>Jonatha Oliveira</p>
              <p style={{ fontSize: '8px', color: '#64748b' }}>Representante — Faço a Conta</p>
              <p style={{ fontSize: '8px', color: '#64748b' }}>Empresa Parceira Executora</p>
            </div>
            {/* Assinatura 3 */}
            <div style={{ textAlign: 'center' as const, minWidth: '130px' }}>
              <div style={{ borderBottom: '1px solid #1e293b', marginBottom: '6px', paddingBottom: '48px' }}></div>
              <p style={{ fontSize: '10px', fontWeight: 800, color: '#1e293b' }}>Joelton Oliveira</p>
              <p style={{ fontSize: '8px', color: '#64748b' }}>Representante — Econoserv Consultoria</p>
              <p style={{ fontSize: '8px', color: '#64748b' }}>Empresa Parceira Executora</p>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div style={{ textAlign: 'center' as const, paddingTop: '12px', borderTop: '1px solid #e2e8f0', marginTop: '16px' }}>
          <p style={{ fontSize: '8px', color: '#94a3b8' }}>Transformação Real 60D — Do diagnóstico à execução. Resultados reais em 60 dias.</p>
        </div>

      </div>
    </div>
  );
});

PropostaHTML.displayName = 'PropostaHTML';